import type { AccessControlProvider } from "@refinedev/core";
import { useMenu as useRefineMenu } from "@refinedev/core";
import type { TreeMenuItem } from "@refinedev/core/dist/hooks/menu/useMenu";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { find } from "lodash-es";
import { useMemo } from "react";
import { type DeviceOverview } from "utils/types/device.type";
import type { Menu } from "utils/types/menu.type";
import type { Property, PropertyService } from "utils/types/object.type";
import { UserRoleType } from "utils/types/user.type";

import { useGlobalStore } from "@/store";

import { axiosInstance } from "./utils";
import { queryClient } from "./utils/queryClient";

type MenuItem = {
  name: string;
  disabled?: boolean;
  error?: string;
};
export const getMenu = async (propertyId: string) => {
  const { roles, setMenus } = useGlobalStore.getState();
  const menus = new Map<string, MenuItem>();

  const { data: propertyData } = await axiosInstance.get<Property>(`/properties/${propertyId}`);
  const { data: menuData } = await axiosInstance.get<Menu[]>(`/properties/${propertyId}/menu`);
  const { data: serviceData } = await axiosInstance.get<PropertyService[]>(`/properties/${propertyId}/services`);
  const getDeviceMenu = async () => {
    const { data: deviceSummaryData } = await axiosInstance.get<DeviceOverview>(
      `/properties/${propertyId}/devices/overview`
    );
    let name = "";
    if (deviceSummaryData.PabbitLite) {
      name = "PabbitLite";
      menus.set(name, { name });
    }
    if (deviceSummaryData.PATMO) {
      name = "PATMO";
      menus.set(name, { name });
    }
    if (deviceSummaryData.dearisController) {
      name = "dearis";
      menus.set(name, { name });
    }
    if (deviceSummaryData.PabbitLocker) {
      name = "locker";
      menus.set(name, { name });
    }
  };

  const servicesMenu = serviceData?.map((item) => {
    if (!item) return { name: "", visibility: false, changeable: false };
    const { type, status } = item;
    if (type === "temporaryPass" && status === "active") {
      return { name: "temp_access", visibility: true, changeable: false };
    } else if (type === "bizKey" && status === "active") {
      return { name: "bizkey", visibility: true, changeable: false };
    } else {
      return { name: "", visibility: false, changeable: false };
    }
  });
  setMenus([...menuData, ...servicesMenu]);

  const propertyType = propertyData.property_type;

  // 设置物件管理者和物件职员可以看到的菜单
  const findRole = find(roles, ({ role }) => [UserRoleType.PropertyManager, UserRoleType.PropertyStaff].includes(role));
  // 有一些功能物件管理者可以操作物件职员不可以, 比如 アカウント管理
  const isPropertyStaff = find(roles, { role: "PropertyStaff", property_id: propertyId });

  if (findRole) {
    // 一级菜单
    menuData
      .filter(({ visibility }) => visibility)
      .forEach(({ name }) => {
        if (name === "account_management") {
          menus.set(name, {
            name,
            disabled: isPropertyStaff ? true : false,
          });
        } else if (name === "customer_group_management") {
          menus.set(name + "_" + propertyType, { name: name + "_" + propertyType });
        } else {
          menus.set(name, { name });
        }
      });
    // 设备二级菜单
    await getDeviceMenu();

    serviceData.forEach(({ type, status }) => {
      if (type === "temporaryPass" && status === "active") {
        menus.set("temp_access", { name: "temp_access" });
      } else if (type === "bizKey" && status === "active") {
        menus.set("bizkey", { name: "bizkey" });
      }
    });
    return menus;
  } else {
    menuData.forEach(({ name }) => {
      if (name === "customer_group_management") {
        menus.set(name + "_" + propertyType, { name: name + "_" + propertyType });
      } else {
        menus.set(name, { name });
      }
    });
    await getDeviceMenu();
    serviceData.forEach(({ type, status }) => {
      if (type === "temporaryPass" && status === "active") {
        menus.set("temp_access", { name: "temp_access" });
      } else if (type === "bizKey" && status === "active") {
        menus.set("bizkey", { name: "bizkey" });
      }
    });
    return menus;
  }
};

export const useMenu = (propertyId?: string) => {
  const { data, isLoading } = useQuery({
    queryKey: ["menu", propertyId],
    queryFn: () => getMenu(propertyId!),
    enabled: !!propertyId,
  });
  const { menuItems, ...rest } = useRefineMenu();
  const parsedMenuItems = useMemo<TreeMenuItem[]>(() => {
    const items = menuItems
      .filter(({ name, meta }) => data?.has(meta?.label ?? name))
      .map((item) => {
        if (item.name === "devices") {
          item.children = item.children.filter(({ name }) => data?.has(name));
        }
        const original = data?.get(item.name);
        if (original && original.disabled) {
          item.meta = { disabled: original.disabled, error: original.error };
        }
        return item;
      });
    return items;
  }, [menuItems, data]);

  return {
    menuItems: parsedMenuItems,
    isLoading,
    ...rest,
  };
};

export const accessControlProvider = {
  async can({ resource, action, params }) {
    const { menus, propertyId } = useGlobalStore.getState();
    const metadata = params?.resource?.meta;

    if (propertyId && menus.length === 0) {
      const queryState = queryClient.getQueryState(["menu", propertyId]);
      if (!queryState || queryState?.status === "pending" || queryState?.fetchStatus === "fetching") {
        try {
          await queryClient.ensureQueryData({
            queryKey: ["menu", propertyId],
            queryFn: () => getMenu(propertyId),
          });
          const { menus: updatedMenus } = useGlobalStore.getState();
          if (updatedMenus.length === 0) {
            return { can: false, reason: "Menu data not loaded" };
          }
        } catch (error) {
          return { can: false, reason: "Failed to load menu data" };
        }
      } else if (queryState?.status === "error" || (queryState?.status === "success" && menus.length === 0)) {
        return { can: false, reason: "Menu data not available" };
      }
    }

    const { menus: currentMenus } = useGlobalStore.getState();
    const allowedResources = new Set<string>();
    allowedResources.add("index");
    allowedResources.add("dashboard");

    const { roles } = useGlobalStore.getState();
    const findRole = find(roles, ({ role }) => [UserRoleType.SystemManager, UserRoleType.TenantManager].includes(role));
    if (findRole) {
      // 系统管理员和Aiphone管理员默认可以访问所有资源;
      // 但是 services还是需要根据物件是否开启来判断;
      for (const { name, visibility } of currentMenus) {
        if (name === "delivery_provider_management") {
          allowedResources.add("delivery");
        } else if (name === "customer_group_management") {
          allowedResources.add("customer_group_management_mansion");
          allowedResources.add("customer_group_management_shareoffice");
        } else if (name === "device_management") {
          allowedResources.add("devices");
        } else if (name === "temp_access" && visibility) {
          allowedResources.add("temp_access");
        } else if (name === "bizkey" && visibility) {
          allowedResources.add("bizkey");
        } else if (name === "account_management") {
          allowedResources.add("account_management");
          allowedResources.add("user_detail");
        } else if (name === "todo_management") {
          allowedResources.add("todo_management");
        }
      }
    } else {
      // 物件员工和物件管理员需要根据接口返回来判断某个资源是否在sider上可见;
      for (const { name, visibility } of currentMenus) {
        if (name === "delivery_provider_management" && visibility) {
          allowedResources.add("delivery");
        } else if (name === "customer_group_management" && visibility) {
          allowedResources.add("customer_group_management_mansion");
          allowedResources.add("customer_group_management_shareoffice");
          allowedResources.add("customer_group_management_notification");
        } else if (name === "device_management" && visibility) {
          allowedResources.add("devices");
        } else if (name === "temp_access" && visibility) {
          allowedResources.add("temp_access");
        } else if (name === "bizkey" && visibility) {
          allowedResources.add("bizkey");
        } else if (name === "account_management" && visibility) {
          allowedResources.add("account_management");
          allowedResources.add("user_detail");
        } else if (name === "todo_management" && visibility) {
          allowedResources.add("todo_management");
        }
      }
    }

    if (["dearis", "lite", "PATMO", "locker"].includes(resource || "")) {
      return { can: true };
    } else if (
      (resource && allowedResources.has(resource)) ||
      (metadata?.parent && allowedResources.has(metadata.parent))
    ) {
      return { can: true };
    } else {
      return { can: false, reason: "In development" };
    }
  },
  options: {
    buttons: {
      enableAccessControl: true,
      hideIfUnauthorized: true,
    },
  },
} satisfies AccessControlProvider;
